import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import React from 'react';
import useFetch from '@maxis/client-logic/hooks/useFetch';
import { validate } from '@maxis/client-logic/utils/validationUtil';
import { useBoundStore } from '@maxis/client-logic/store/useBoundStore';
import formStyles from '@/css/FormSection.module.css';
import { Modal } from '@maxis/components';
import { FormSection } from '@maxis/components';
import { LOCAL_STORAGE_KEYS } from '@maxis/client-logic/constants/enumerates';
import { setLocalStorageItem } from '@maxis/client-logic/utils/localStorageUtil';
import useMessageBox from '@maxis/client-logic/hooks/useMessageBox';
import { clear } from '@maxis/client-logic/utils/controlsUtil';
import useAuth from '@maxis/client-logic/hooks/useAuth';
import pageStyles from '@/css/PageContent.module.css';

const PWChangePopup = React.memo(
    forwardRef((props, ref) => {
        const { request } = useFetch();
        const { userId, userNm, expiredCheck, userEmail } = useBoundStore((state) => state.user);
        const { alert, confirm } = useMessageBox();
        const { logout } = useAuth();

        useImperativeHandle(ref, () => ({
            showPopup: () => {
                clear(controls.form);
                controls.refPopup.current.showPopup();
            },
        }));

        useEffect(() => {
            if (expiredCheck == 'Y') {
                clear(controls.form);
                controls.refPopup.current.showPopup();
            }
        }, [expiredCheck]);

        const controls = {
            refPopup: useRef(),
            form: {
                formUser: {
                    userId: {
                        ref: useRef(),
                        label: '사용자 아이디',
                        labelWidth: 'wpx_180',
                        afterText: { ref: useRef(), text: <span> &nbsp;&nbsp;{userId}</span> },
                    },
                    userNm: {
                        ref: useRef(),
                        label: '사용자 명',
                        labelWidth: 'wpx_180',
                        afterText: { ref: useRef(), text: <span> &nbsp;&nbsp;{userNm}</span> },
                    },
                    userEmail: {
                        ref: useRef(),
                        label: '이메일',
                        labelWidth: 'wpx_180',
                        afterText: { ref: useRef(), text: <span> &nbsp;&nbsp;{userEmail}</span> },
                    },
                },
                formPassword: {
                    txtOriginPassword: {
                        ref: useRef(),
                        label: '기존 비밀번호',
                        labelWidth: 'wpx_180',
                        validation: { required: true },
                        controlOption: {
                            type: 'password',
                            placeholder: '기존 비밀번호를 입력하세요.',
                        },
                    },
                    txtNewPassword: {
                        ref: useRef(),
                        label: '신규 비밀번호',
                        labelWidth: 'wpx_180',
                        validation: { required: true },
                        controlOption: {
                            type: 'password',
                            placeholder: '신규 비밀번호를 입력하세요.',
                        },
                    },
                    txtChkPassword: {
                        ref: useRef(),
                        label: '신규 비밀번호 확인',
                        labelWidth: 'wpx_180',
                        validation: { required: true },
                        controlOption: {
                            type: 'password',
                            placeholder: '신규 비밀번호를 다시 입력하세요.',
                        },
                    },
                },
            },
            buttons: {
                btnPWChange: {
                    checkAuth: false,
                    label: '비밀번호변경',
                    onClick: () => {
                        pageFunctions.pwChange();
                    },
                },
            },
        };

        const popupFunctions = {
            validatePW: async () => {
                const newPW = controls.form.formPassword.txtNewPassword.ref;
                const chkPW = controls.form.formPassword.txtChkPassword.ref;

                if (validate(controls.form)) {
                    if (newPW.current?.getValue() !== chkPW.current?.getValue()) {
                        alert('', '비밀번호 확인이 일치하지 않습니다.');
                        throw new Error('비밀번호 확인이 일치하지 않습니다.');
                    }
                }
            },
            validChk: async () => {
                const chkPW = controls.form.formPassword.txtChkPassword.ref.current?.getValue() ?? '';

                if (chkPW.length < 10) {
                    alert('', '비밀번호는 10자 이상입니다.');
                    throw new Error('비밀번호는 10자 이상입니다.');
                } else if (chkPW.length > 30) {
                    alert('', '비밀번호는 30자 이하입니다.');
                    throw new Error('비밀번호는 30자 이하입니다.');
                }

                if ((chkPW.match(/[\p{Script=Hangul}]/u) || []).length) {
                    alert('', '비밀번호에 한글은 사용할 수 없습니다.');
                    throw new Error('비밀번호에 한글은 사용할 수 없습니다.');
                } else {
                    const chkItem = {
                        digit: (chkPW.match(/\d/g) || []).length,
                        lower: (chkPW.match(/[a-z]/g) || []).length,
                        upper: (chkPW.match(/[A-Z]/g) || []).length,
                        special: (
                            chkPW.match(
                                /[\!\@\#\$\%\^\&\*\(\)\-\_\=\~\:\;\<\>\.\,\{\}\[\]\\\|\'\`\?]/g
                            ) || []
                        ).length,
                    };

                    let cnt = 0;
                    for (let i in chkItem) {
                        if (chkItem[i] > 0) cnt++;
                    }

                    if (cnt < 3) {
                        alert(
                            '',
                            '비밀번호는 대문자, 소문자, 특수문자, 숫자 중 3가지 이상 조합해야 합니다.'
                        );
                        throw new Error(
                            '비밀번호는 대문자, 소문자, 특수문자, 숫자 중 3가지 이상 조합해야 합니다.'
                        );
                    }
                }
            },
            commonValidChk: async () => {
                const chkPW = controls.form.formPassword.txtChkPassword.ref.current?.getValue() ?? '';
                const regex = /^[0-9]+$/;

                if (chkPW.length < 6) {
                    alert('', '비밀번호는 6자 이상입니다.');
                    throw new Error('비밀번호는 6자 이상입니다.');
                }

                if (!regex.test(chkPW)) {
                    alert('', '비밀번호는 숫자만 가능합니다.');
                    throw new Error('비밀번호는 숫자만 가능합니다.');
                }
            },
            pwChange: async () => {
                try {
                    await popupFunctions.validatePW();
                    await popupFunctions.validChk();

                    const confirmResult = await confirm('확인', '비밀번호를 변경하시겠습니까?');

                    if (confirmResult) {
                        const requestOptions = {
                            method: 'POST',
                            body: {
                                USER_ID: userId,
                                PASSWORD:
                                    controls.form.formPassword.txtOriginPassword.ref.current?.getValue(),
                                NEW_PASSWORD:
                                    controls.form.formPassword.txtNewPassword.ref.current?.getValue(),
                            },
                        };
                        const messageOptions = {
                            showAlert: true,
                            alertTitle: '오류',
                            showSuccess: false,
                        };
                        const response = await request(
                            // '/user/password-change',
                            '/E3/SYSTEM/ChangePassword',
                            requestOptions,
                            messageOptions
                        );
                        if (response) {
                            setLocalStorageItem(
                                LOCAL_STORAGE_KEYS.ACCESS_TOKEN,
                                response.USER.ACCESS_TOKEN
                            );
                            await popupFunctions.initForm();
                        }
                    }
                } catch (e) {
                    alert('', e.message);
                }
            },
            initForm: async () => {
                await clear(controls.form, true);
                await controls.refPopup.current.closePopup();
                await logout(userId);
            },
        };
        return (
            <Modal
                ref={controls.refPopup}
                defaultTitle={'내정보'}
                confirmBtnLabel="비밀번호 변경"
                confirmBtnEvent={popupFunctions.pwChange}
                defaultHeight="500px"
                defaultWidth="550px"
                {...(expiredCheck === 'N' && { closeBtnEvent: () => {} })}
                endEvent={() => clear(controls.form)}
            >
                <div className={pageStyles.divTitle_lv4}>사용자 정보</div>
                <div style={{ borderTop: '1px solid #ddd' }}></div>
                <FormSection formControls={controls.form.formUser} numOfCols={1} />

                <div style={{ marginBottom: '20px' }}></div>

                <div className={pageStyles.divTitle_lv4}>비밀번호 변경</div>
                <div style={{ borderTop: '1px solid #ddd' }}></div>
                <FormSection formControls={controls.form.formPassword} numOfCols={1} />

                <div className={formStyles.description}>
                    <label style={{ color: 'red' }}>대문자, 소문자, 특수문자, 숫자</label> 중 3가지
                    이상 조합하여 10자 이상을 사용하세요
                </div>
            </Modal>
        );
    })
);

PWChangePopup.displayName = 'PWChangePopup';
export default PWChangePopup;
